import React, { useRef, useEffect } from "react";

const BAR_WIDTH = 4; // Fixed bar width in pixels
const BAR_SPACING = 2; // Spacing between bars

export const VolumeHistogram = ({
  audioStream,
}: {
  audioStream: MediaStream | null;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const analyserRef = useRef<AnalyserNode>();
  const volumeHistory = useRef<number[]>([]);
  const updateCounter = useRef<number>(0);
  const containerWidth = useRef<number>(0);

  // Update container width
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateSize = () => {
      if (containerRef.current) {
        containerWidth.current = containerRef.current.clientWidth;
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    
    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  useEffect(() => {
    if (!audioStream || !canvasRef.current) return;

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(audioStream);

    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.3;
    source.connect(analyser);
    analyserRef.current = analyser;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d")!;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    const canvasHeight = 45;
    const dpr = window.devicePixelRatio || 1; // Device pixel ratio for sharp rendering

    volumeHistory.current = [];
    updateCounter.current = 0;

    const draw = () => {
      if (!analyserRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArray);

      // Update every 3 frames
      updateCounter.current++;
      if (updateCounter.current % 3 === 0) {
        // Calculate average volume (RMS)
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i]! * dataArray[i]!;
        }
        const rms = Math.sqrt(sum / dataArray.length);
        const volume = rms / 255; // Normalize to 0-1

        // Add new volume to history
        volumeHistory.current.push(volume);
        
        // Keep only enough bars to fill container plus some extra for smooth scrolling
        const maxBars = Math.ceil(containerWidth.current / (BAR_WIDTH + BAR_SPACING)) + 20;
        if (volumeHistory.current.length > maxBars) {
          volumeHistory.current.shift();
        }
      }

      // Calculate canvas width based on number of bars
      const history = volumeHistory.current;
      const displayWidth = history.length * (BAR_WIDTH + BAR_SPACING);
      
      // Set actual canvas size (accounting for device pixel ratio)
      if (canvas.width !== displayWidth * dpr) {
        canvas.width = displayWidth * dpr;
        canvas.height = canvasHeight * dpr;
        // Scale context to account for device pixel ratio
        ctx.scale(dpr, dpr);
      }
      
      // Set CSS size (displayed size)
      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${canvasHeight}px`;

      // Clear canvas
      ctx.fillStyle = "#f9fafb";
      ctx.fillRect(0, 0, displayWidth, canvasHeight);

      // Draw volume bars from left to right
      for (let i = 0; i < history.length; i++) {
        const barHeight = history[i]! * canvasHeight * 0.8;
        const x = i * (BAR_WIDTH + BAR_SPACING);
        const y = canvasHeight - barHeight;

          // Color based on volume level - using brand colors
          ctx.fillStyle = "#2A1745";

        ctx.fillRect(x, y, BAR_WIDTH, barHeight);
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      audioContext.close();
    };
  }, [audioStream]);

  return (
    <div 
      ref={containerRef}
      className="flex-1 overflow-hidden rounded-3xl bg-gray-50 h-[45px] flex items-center"
    >
      <div className="w-full h-full overflow-x-hidden flex justify-end">
        <canvas
          ref={canvasRef}
          className="h-[45px]"
        />
      </div>
    </div>
  );
};

